import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config';

const DeleteEventComponentsPage = () => {
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [eventConfig, setEventConfig] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [error, setError] = useState(null);

  const [confirmAI, setConfirmAI] = useState(false);
  const [deprovisioning, setDeprovisioning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoadingEvents(true);
      setError(null);

      const [activeResp, eventsResp] = await Promise.all([
        fetch(`${CONFIG.apiBaseUrl}/active-event`, { credentials: 'include' }),
        fetch(`${CONFIG.apiBaseUrl}/admin/events`, { credentials: 'include' }),
      ]);

      let activeId = null;
      if (activeResp.ok) {
        const activeData = await activeResp.json();
        activeId = activeData.activeEventId;
      }

      let events = [];
      if (eventsResp.ok) {
        const eventsData = await eventsResp.json();
        events = eventsData.events || [];
      }

      setAvailableEvents(events);

      const defaultId = activeId || (events.length > 0 ? events[0].event_id : null);
      setSelectedEventId(defaultId);

      if (defaultId) {
        await loadEventConfig(defaultId);
      }
    } catch (err) {
      console.error('loadInitialData failed:', err);
      setError(err.message);
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadEventConfig = async (id) => {
    try {
      setLoadingConfig(true);
      setError(null);
      setEventConfig(null);
      setResult(null);
      setConfirmAI(false);

      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(id)}`, {
        credentials: 'include',
      });
      if (!resp.ok) {
        const d = await resp.json();
        throw new Error(d.error || d.message || 'Failed to fetch event details');
      }
      const data = await resp.json();
      setEventConfig(data.event || data);
    } catch (err) {
      console.error('loadEventConfig failed:', err);
      setError(err.message);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleEventChange = (newId) => {
    setSelectedEventId(newId);
    loadEventConfig(newId);
  };

  const handleDeprovisionAI = async () => {
    setConfirmAI(false);
    setDeprovisioning(true);
    setResult(null);
    try {
      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/admin/events/${encodeURIComponent(selectedEventId)}/deprovision-ai`,
        { method: 'POST', credentials: 'include' }
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || data.error || `Delete failed (${resp.status})`);
      setResult(data);
      // Refresh event config to reflect cleared fields
      await loadEventConfig(selectedEventId);
    } catch (err) {
      console.error('deprovisionAI failed:', err);
      setResult({ success: false, error: err.message });
    } finally {
      setDeprovisioning(false);
    }
  };

  const hasAIComponents = eventConfig && (
    eventConfig.lex_bot_id || eventConfig.bedrock_agent_id || eventConfig.bedrock_kb_id ||
    eventConfig.ai_provisioning_status === 'error'
  );

  const provisioningInProgress = eventConfig?.ai_provisioning_status === 'pending';

  const statusBadge = (label, active, trueColor = '#2e7d32', falseColor = '#666') => (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: 600,
      background: active ? trueColor : falseColor,
      color: '#fff',
      marginRight: '8px',
    }}>
      {label}
    </span>
  );

  const stepLabel = (status) => {
    if (!status) return null;
    const color = status === 'deleted' ? '#2e7d32'
      : status === 'not_found' || status === 'skipped' ? '#888'
      : '#c62828';
    return (
      <span style={{ color, fontWeight: 600, fontSize: '0.85rem' }}>
        {status}
      </span>
    );
  };

  const STEP_LABELS = {
    bedrock_agent_alias: 'Bedrock Agent Alias',
    bedrock_agent: 'Bedrock Agent',
    bedrock_kb_data_source: 'Knowledge Base Data Source',
    bedrock_kb: 'Knowledge Base',
    s3_vectors_index: 'S3 Vectors Index',
    lex_bot_alias: 'Lex Bot Alias',
    lex_bot: 'Lex Bot',
    s3_kb_documents: 'S3 KB Documents',
    event_record: 'Event Record',
  };

  return (
    <div className="whatsapp-responses-page">
      <h2 className="page-title">Delete Event Components</h2>
      <p className="page-subtitle">Permanently remove provisioned AWS resources for an event. This cannot be undone.</p>

      {loadingEvents && <div className="loading-message">Loading events...</div>}
      {error && <div className="error-message">Error: {error}</div>}

      {!loadingEvents && availableEvents.length === 0 && (
        <div className="no-messages">No events found.</div>
      )}

      {!loadingEvents && availableEvents.length > 0 && (
        <>
          {/* Event selector */}
          <div className="settings-section">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor="eventSelect" className="form-label">Select Event</label>
              <select
                id="eventSelect"
                value={selectedEventId || ''}
                onChange={(e) => handleEventChange(e.target.value)}
                className="form-input"
                disabled={loadingConfig || deprovisioning}
              >
                {availableEvents.map((ev) => (
                  <option key={ev.event_id} value={ev.event_id}>
                    {ev.event_name || ev.event_id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loadingConfig && <div className="loading-message">Loading event details...</div>}

          {!loadingConfig && eventConfig && (
            <>
              {/* AI & Lex Components */}
              <div className="settings-section">
                <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1rem', fontWeight: 600 }}>
                  AI &amp; Lex Components
                </h3>

                <div style={{ marginBottom: '16px' }}>
                  {statusBadge(
                    eventConfig.lex_enabled ? 'Lex Enabled' : 'Lex Disabled',
                    eventConfig.lex_enabled,
                  )}
                  {eventConfig.ai_provisioning_status && statusBadge(
                    `AI: ${eventConfig.ai_provisioning_status}`,
                    eventConfig.ai_provisioning_status === 'ready',
                    eventConfig.ai_provisioning_status === 'error' ? '#c62828' : '#1565c0'
                  )}
                </div>

                {/* Resource list */}
                <div style={{ marginBottom: '16px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <tbody>
                      {[
                        ['Lex Bot ID', eventConfig.lex_bot_id],
                        ['Bedrock Agent ID', eventConfig.bedrock_agent_id],
                        ['Knowledge Base ID', eventConfig.bedrock_kb_id],
                      ].map(([label, value]) => (
                        <tr key={label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '6px 12px', color: '#666', width: '40%' }}>{label}</td>
                          <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.8rem', color: value ? '#333' : '#aaa' }}>
                            {value || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Provisioning in progress warning */}
                {provisioningInProgress && (
                  <div className="error-message" style={{ marginBottom: '12px' }}>
                    AI provisioning is currently in progress. Wait for it to complete before deleting.
                  </div>
                )}

                {/* No components notice */}
                {!hasAIComponents && !result && (
                  <div className="no-messages">
                    No AI components are provisioned for this event.
                  </div>
                )}

                {/* Result display */}
                {result && (
                  <div
                    className={result.success ? 'success-message' : 'error-message'}
                    style={{ marginBottom: '12px' }}
                  >
                    <strong>{result.success ? 'Deletion complete' : 'Deletion failed'}:</strong>{' '}
                    {result.message || result.error}
                  </div>
                )}

                {result?.details && (
                  <div style={{ marginBottom: '16px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600 }}>Component</th>
                          <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(result.details).map(([key, value]) => (
                          <tr key={key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '6px 12px', color: '#555' }}>
                              {STEP_LABELS[key] || key}
                            </td>
                            <td style={{ padding: '6px 12px' }}>
                              {stepLabel(value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Action button */}
                {hasAIComponents && !deprovisioning && !provisioningInProgress && (
                  <button
                    onClick={() => setConfirmAI(true)}
                    style={{
                      padding: '8px 18px',
                      background: 'none',
                      color: '#c62828',
                      border: '1px solid #c62828',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                    }}
                  >
                    Delete AI &amp; Lex Components
                  </button>
                )}

                {deprovisioning && (
                  <div style={{ padding: '8px 0', color: '#555', fontStyle: 'italic' }}>
                    Deleting AI components... this may take up to a minute.
                  </div>
                )}

                {/* Confirmation dialog */}
                {confirmAI && (
                  <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    background: '#fff8f8',
                    border: '1px solid #f5c6c6',
                    borderRadius: '6px',
                  }}>
                    <p style={{ margin: '0 0 12px', fontWeight: 600, color: '#c62828' }}>
                      Confirm deletion
                    </p>
                    <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: '#444' }}>
                      This will permanently delete the Lex bot, Bedrock agent, Knowledge Base, S3 Vectors index,
                      and all uploaded KB documents for <strong>{eventConfig.event_name || selectedEventId}</strong>.
                      Lex will be disabled on the event. This cannot be undone.
                    </p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={handleDeprovisionAI}
                        style={{
                          padding: '8px 18px',
                          background: '#c62828',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}
                      >
                        Confirm Delete
                      </button>
                      <button
                        onClick={() => setConfirmAI(false)}
                        className="btn-refresh"
                        style={{ fontSize: '0.9rem' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* #HeyDJ Components — Coming Soon */}
              <div
                className="settings-section"
                style={{ opacity: 0.55, pointerEvents: 'none' }}
              >
                <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '1rem', fontWeight: 600 }}>
                  #HeyDJ Components{' '}
                  <span style={{ color: '#999', fontWeight: 400, fontSize: '0.85rem' }}>(Coming Soon)</span>
                </h3>
                <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                  Delete all HeyDJ song request data and disable the feature for this event.
                </p>
              </div>

              {/* Media Components — Coming Soon */}
              <div
                className="settings-section"
                style={{ opacity: 0.55, pointerEvents: 'none' }}
              >
                <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '1rem', fontWeight: 600 }}>
                  Media Components{' '}
                  <span style={{ color: '#999', fontWeight: 400, fontSize: '0.85rem' }}>(Coming Soon)</span>
                </h3>
                <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                  Delete all stored media files and disable media collection for this event.
                </p>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DeleteEventComponentsPage;
